/*!
 * sceneChain.js — a score's next: hand-overs as numbered steps
 * © 2026 Rob Canning — GPLv3
 *
 * The pure half of sceneReadout.js: given every cue that hands over (the
 * names it answers to, and the name its next: gives), number the steps by
 * walking the links from each head (a step nothing hands over to). A chain
 * that loops or forks still gets every step a number; the order is just the
 * walk order.
 */

/**
 * @param {{ names: string[], to: string }[]} handovers  one per handing-over
 *   element: its uid and element id (either may be what next: uses), and its target
 * @returns {{ map: Map<string, {next: string|null, index: number}>, alias: Map<string, string>, total: number }}
 */
export function chainFromLinks(handovers) {
  const targets = new Set(handovers.map(h => h.to));

  // One element is one step, but it answers to two names: its uid: and its
  // element id. Whoever wrote the next: picked one of them. Settle on a
  // canonical name, or a step addressed by its id while it hands over by its
  // uid reads as two steps — and as the end of the chain, since the outgoing
  // link hangs off the name nobody used.
  const alias = new Map();          // any name -> canonical
  const links = new Map();          // canonical -> next (a name, resolved below)
  for (const h of handovers) {
    const canon = h.names.find(n => targets.has(n)) ?? h.names[0];
    for (const n of h.names) alias.set(n, canon);
    links.set(canon, h.to);
  }
  for (const t of targets) if (!alias.has(t)) alias.set(t, t);   // a step that ends the chain
  for (const [from, to] of links) links.set(from, alias.get(to) ?? to);

  const nodes = new Set([...links.keys(), ...alias.values()]);
  const incoming = new Set(links.values());
  const order = [];
  const seen = new Set();
  const walk = (n) => {
    while (n && !seen.has(n)) { seen.add(n); order.push(n); n = links.get(n); }
  };
  [...nodes].filter(n => !incoming.has(n)).forEach(walk);   // heads first
  [...nodes].forEach(walk);                                  // loops and islands

  const map = new Map();
  order.forEach((name, i) => map.set(name, { next: links.get(name) ?? null, index: i + 1 }));
  return { map, alias, total: order.length };
}
